import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiCalendar } from "react-icons/fi";
import { AnalyticsHeader } from "@/domains/analytics/components/ds/AnalyticsHeader";
import { CycleHero } from "@/domains/analytics/components/ds/CycleHero";
import { SplitSummary } from "@/domains/analytics/components/ds/SplitSummary";
import { SortPills, Tabs } from "@/components/ds";
import { NewBudgetModal } from "@/domains/budget/components/NewBudgetModal";
import { useAnalyticsData } from "@/domains/analytics/hooks/useAnalyticsData";
import { useCycleSpendBreakdown } from "@/domains/analytics/hooks/useCycleSpendBreakdown";
import { useCategoryTransactions } from "@/domains/analytics/hooks/useAnalyticsQueries";
import { SavingsDistributionCard } from "@/domains/analytics/components/AnalyticsCards";
import type { SeriesDatum } from "@/domains/analytics/components/charts/seriesShare";
import { CategoryDetailModal } from "@/domains/analytics/components/CategoryDetailModal";
import { buildColorMap, toShareSegments } from "@/domains/analytics/components/charts/seriesShare";
import { ShareSummary } from "@/domains/analytics/components/ShareSummary";
import { FlowChartWide, type FlowSeries } from "@/domains/analytics/components/ds/FlowChartWide";
import { ModeBar } from "@/domains/analytics/components/ds/ModeBar";
import { RankingRow } from "@/domains/analytics/components/ds/RankingRow";
import { SeriesGroup, SeriesRow } from "@/domains/analytics/components/ds/SeriesRow";
import { useCategoryMonthlyFlow, type TrendCategoryOption } from "@/domains/analytics/hooks/useCategoryMonthlyFlow";
import {
  availableRankingSorts,
  sortRankingItems,
  type CategoryRankingMeta,
  type PieMetric,
  type RankingSort,
} from "@/domains/analytics/utils/categoryRanking";
import { showAppMessage } from "@/components/ui/confirmDialog";
import { FormField, SelectInput, SkeletonBlock } from "@/components/ui/primitives";
import {
  AnalyticsCard,
  DistributionBar,
  InfoNote,
  MetricTile,
  PillButton,
  SectionIntro,
  TotalLine,
  type DistributionSegment,
} from "@/components/ds";
import { PageSkeleton } from "@/components/ui/PageSkeleton";
import { FEATURE_FLAGS } from "@/config/featureFlags";
import {
  getBudgetUsageLabel,
  getOverspendMessage,
} from "@/domains/analytics/utils/budgetInsights";
import {
  useCategoryInsights,
  type CategoryInsight,
} from "@/domains/analytics/hooks/useCategoryInsights";
import {
  buildCategoryTransactionBreakdown,
  buildPieData,
  buildSavingsDistributionData,
  isIncompleteInstallment,
  toggleHiddenItem,
} from "@/domains/analytics/utils/analyticsPresentation";
import {
  parseDateLocal,
  resolveMonthInputRange,
  resolveRelativeMonthRange,
  toMonthInputValue,
} from "@/utils/dateUtils";
import { formatCurrency, formatDate, formatPercent, formatSignedCurrency } from "@/utils/formatters";
import { useNavigationState } from "@/hooks/useNavigationState";
import { useMonthlyBalances, useTransactionTypes } from "@/hooks/useTransactions";
import { useBudgetEditor } from "@/domains/analytics/hooks/useBudgetEditor";

type CycleFilter = "current" | "previous" | "custom";
type AnalyticsViewTab = "summary" | "categories" | "flow" | "installments";

type HiddenPieItems = Array<number | string>;

type EnrichedCategoryBalance = {
  id: number;
  name: string;
  category_total_expense?: number;
  category_total_deposit?: number;
};

const PIE_COLORS = [
  "var(--app-chart-series-1)",
  "var(--app-chart-series-2)",
  "var(--app-chart-series-3)",
  "var(--app-chart-series-4)",
  "var(--app-chart-series-5)",
];

/**
 * A string id keeps this slice out of anything keyed by category: the ranking will not make
 * it clickable, and no budget or projection meta can be looked up for it.
 */
const UNCATEGORIZED_ID = "uncategorized";

const withUncategorized = (
  data: SeriesDatum[],
  amount: number,
  label: string,
): SeriesDatum[] => {
  const value = Number(amount ?? 0);
  if (value <= 0) return data;

  return [...data, { id: UNCATEGORIZED_ID, name: label, value }].sort(
    (first, second) => second.value - first.value,
  );
};

export const AnalyticsView = () => {
  const { t } = useTranslation(["analytics", "common"]);

  const [cycle, setCycle] = useNavigationState<CycleFilter>("analytics.cycle", "current");
  const [analyticsView, setAnalyticsView] = useNavigationState<AnalyticsViewTab>(
    "analytics.view",
    "summary",
  );
  const [customMonth, setCustomMonth] = useNavigationState<string>(
    "analytics.cycleMonth",
    toMonthInputValue(new Date()),
  );

  const [cycleMenuOpen, setCycleMenuOpen] = useState(false);
  const cycleMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!cycleMenuOpen) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!cycleMenuRef.current?.contains(event.target as Node)) setCycleMenuOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, [cycleMenuOpen]);

  // Only months the user actually moved money in. Offering a fixed window would list
  // months that render an empty page, and it would also miss months past today: billing
  // cycles push credit-card charges into future months that do have data.
  const { data: monthlyBalanceData } = useMonthlyBalances();

  const selectableMonths = useMemo(() => {
    const toOption = (date: Date) => ({
      value: toMonthInputValue(date),
      label: formatDate(date, undefined, { month: "long", year: "numeric" }),
    });
    const months = (monthlyBalanceData?.monthly_balances ?? [])
      .map((balance) => toOption(parseDateLocal(balance.date)))
      .filter((month, index, all) => all.findIndex((item) => item.value === month.value) === index)
      .sort((first, second) => second.value.localeCompare(first.value));

    // Keeps the select from rendering blank when the stored month has no movement, which
    // happens after every deletion and on a first visit before the balances resolve.
    if (customMonth && !months.some((month) => month.value === customMonth)) {
      months.unshift(toOption(parseDateLocal(resolveMonthInputRange(customMonth).start)));
    }

    return months;
  }, [customMonth, monthlyBalanceData?.monthly_balances]);
  const [pieMetric, setPieMetric] =
    useNavigationState<PieMetric>("analytics.categoryPieMetric", "expense");
  const [hiddenCategoryIds, setHiddenCategoryIds] = useNavigationState<HiddenPieItems>(
    "analytics.hiddenCategoryIds", [],
  );
  const [hiddenInstallmentIds, setHiddenInstallmentIds] = useNavigationState<HiddenPieItems>(
    "analytics.hiddenInstallmentIds", [],
  );
  const [rankingSort, setRankingSort] = useNavigationState<RankingSort>(
    "analytics.rankingSort", "amount",
  );
  const [hiddenAccountIds, setHiddenAccountIds] = useState<HiddenPieItems>([]);
  const [flowSeries, setFlowSeries] = useState<FlowSeries>("expense");
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    null,
  );
  const budgetEditor = useBudgetEditor();

  // Always a whole month: every block below reads as one cycle, so a day-level range would
  // leave the comparison metadata and cycle KPIs describing different periods.
  const cycleRange = useMemo(() => {
    if (cycle === "custom") return resolveMonthInputRange(customMonth);
    return resolveRelativeMonthRange(cycle === "previous" ? "last-month" : "this-month");
  }, [cycle, customMonth]);

  const {
    categoryBalanceData,
    loadingCategoryBalance,
    projectionData,
    categories,
    budgetData,
    loadingBudgetBalance,
    accountsBalanceData,
    loadingAccountsBalance,
    accounts,
    installmentsBalanceData,
    loadingInstallmentsBalance,
    savingPockets,
    isLoadingSavingPockets,
    savingInstitutions,
  } = useAnalyticsData(cycleRange);

  const enrichedCategories = useMemo<EnrichedCategoryBalance[]>(() => {
    const rawCategories = categoryBalanceData?.categories_balance ?? [];
    return rawCategories.map((category: EnrichedCategoryBalance) => {
      const fullCategory = categories.find(
        (item: { id: number }) => item.id === category.id,
      );
      return {
        ...category,
        name:
          fullCategory?.name ??
          category.name ??
          t("common.empty.uncategorized"),
      };
    });
  }, [categoryBalanceData?.categories_balance, categories, t]);

  const categoryExpensePieData = useMemo<SeriesDatum[]>(() => {
    return withUncategorized(
      buildPieData(
        enrichedCategories,
        (category) => category.id,
        (category) => category.name,
        (category) => Number(category.category_total_expense ?? 0),
      ),
      Number(categoryBalanceData?.uncategorized_total_expense ?? 0),
      t("common.empty.uncategorized"),
    );
  }, [enrichedCategories, categoryBalanceData?.uncategorized_total_expense, t]);

  const categoryIncomePieData = useMemo<SeriesDatum[]>(() => {
    return withUncategorized(
      buildPieData(
        enrichedCategories,
        (category) => category.id,
        (category) => category.name,
        (category) => Number(category.category_total_deposit ?? 0),
      ),
      Number(categoryBalanceData?.uncategorized_total_deposit ?? 0),
      t("common.empty.uncategorized"),
    );
  }, [enrichedCategories, categoryBalanceData?.uncategorized_total_deposit, t]);

  const categoryPieData =
    pieMetric === "income"
      ? categoryIncomePieData
      : categoryExpensePieData;

  const accountExpensePieData = useMemo<SeriesDatum[]>(() => {
    return buildPieData(
      accountsBalanceData?.accounts_balance ?? [],
      (account) => account.account_id,
      (account) => account.name,
      (account) => Number(account.monthly_expense ?? 0),
    );
  }, [accountsBalanceData?.accounts_balance]);

  const accountIncomePieData = useMemo<SeriesDatum[]>(() => {
    return buildPieData(
      accountsBalanceData?.accounts_balance ?? [],
      (account) => account.account_id,
      (account) => account.name,
      (account) => Number(account.monthly_deposit ?? 0),
    );
  }, [accountsBalanceData?.accounts_balance]);

  const accountNameById = useMemo(
    () => new Map(accounts.map((account) => [account.id, account.name])),
    [accounts],
  );

  const activeInstallmentPieData = useMemo<SeriesDatum[]>(() => {
    return (installmentsBalanceData?.installments_balance ?? [])
      .filter(isIncompleteInstallment)
      .map((installment) => ({
        id: installment.id,
        name: installment.name,
        value: Number(installment.amount_left ?? 0),
      }))
      .filter((item) => item.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [installmentsBalanceData?.installments_balance]);

  const monthlyInstallmentPieData = useMemo<SeriesDatum[]>(() => {
    return (installmentsBalanceData?.installments_balance ?? [])
      .filter(isIncompleteInstallment)
      .map((installment) => ({
        id: installment.id,
        name: installment.name,
        value: Number(installment.monthly_expected ?? 0),
      }))
      .filter((item) => item.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [installmentsBalanceData?.installments_balance]);

  const totalCategoryIncome = useMemo(
    () => categoryIncomePieData.reduce((sum, item) => sum + item.value, 0),
    [categoryIncomePieData],
  );

  const visibleCategoryPieData = useMemo(
    () =>
      categoryPieData.filter((item) => !hiddenCategoryIds.includes(item.id)),
    [categoryPieData, hiddenCategoryIds],
  );

  const visibleCategoryTotal = useMemo(
    () => visibleCategoryPieData.reduce((sum, item) => sum + item.value, 0),
    [visibleCategoryPieData],
  );

  const categoryColorById = useMemo(
    () => buildColorMap(categoryPieData, PIE_COLORS),
    [categoryPieData],
  );

  const savingsDistributionData = useMemo<SeriesDatum[]>(() => {
    if (!FEATURE_FLAGS.savingPockets) return [];
    return buildSavingsDistributionData(
      savingPockets,
      savingInstitutions,
      t("common.empty.unknown", { defaultValue: "Unknown" }),
    );
  }, [savingInstitutions, savingPockets, t]);

  const savingsDistributionTotal = useMemo(
    () => savingsDistributionData.reduce((sum, item) => sum + item.value, 0),
    [savingsDistributionData],
  );

  const savingsDistributionColors = useMemo(
    () => buildColorMap(savingsDistributionData, PIE_COLORS),
    [savingsDistributionData],
  );

  const { categoryInsights } = useCategoryInsights(
    enrichedCategories,
    budgetData?.budgets_balance ?? [],
  );

  // Buckets come from the transactions themselves rather than /categories/balance so the
  // KPI above and this card can never disagree about what "cycle spend" means.
  const {
    breakdown: cycleSpendBreakdown,
    budgetPressure,
    isLoading: loadingCycleSpend,
  } = useCycleSpendBreakdown(
    cycleRange,
    budgetData?.budgets_balance ?? [],
    categories,
  );

  // The one category the whole page points at. Everything that used to rank by total spend
  // now reads from here, so the card, the ranking and the summary nudge cannot disagree.
  const topDiscretionaryCategory = budgetPressure.topOverspentCategory;

  // Feeds the single ranking's sort modes, replacing the separate overspend and
  // projection-variance lists that used to rank the same categories elsewhere.
  const categoryRankingMeta = useMemo(() => {
    const projections = projectionData?.category_projections ?? [];
    // Match on id; uncategorized rows come back with a null id, so name is the fallback.
    const varianceById = new Map(
      projections
        .filter((projection) => projection.category_id != null)
        .map((projection) => [
          projection.category_id as number,
          Number(projection.variance_from_average ?? 0),
        ]),
    );
    const varianceByName = new Map(
      projections.map((projection) => [
        projection.category_name,
        Number(projection.variance_from_average ?? 0),
      ]),
    );

    // Overage comes from the discretionary breakdown, not from total spend: a category
    // made entirely of commitments cannot be cut, so ranking it first sent the user after
    // money they have no way to reclaim.
    const overageById = new Map(
      budgetPressure.overspentCategories.map((category) => [category.id, category.overage]),
    );

    return new Map<number | string, CategoryRankingMeta>(
      categoryInsights.map((category) => [
        category.id,
        {
          overage: overageById.get(category.id) ?? 0,
          variance: varianceById.get(category.id) ?? varianceByName.get(category.name),
        },
      ]),
    );
  }, [budgetPressure.overspentCategories, categoryInsights, projectionData?.category_projections]);

  const selectedCategory = useMemo(
    () =>
      categoryInsights.find((category) => category.id === selectedCategoryId) ??
      null,
    [categoryInsights, selectedCategoryId],
  );

  const {
    data: selectedCategoryTransactions = [],
    isLoading: loadingCategoryTransactions,
  } = useCategoryTransactions({
    categoryId: selectedCategory?.id ?? null,
    start: cycleRange.start,
    end: cycleRange.end,
  });

  const trendCategoryOptions = useMemo<TrendCategoryOption[]>(
    () =>
      [...categories]
        .map((category) => ({
          id: Number(category.id),
          name: category.name,
        }))
        .filter((category) => category.id > 0 && category.name)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [categories],
  );

  // Opens on the same category the card and the summary point at. Falling back to
  // categoryInsights[0] would rank by budget usage on total spend — a third ordering that
  // can land on an installments-only category nobody can act on.
  const defaultTrendCategoryId = useMemo(
    () =>
      topDiscretionaryCategory?.id ?? categoryInsights[0]?.id ?? trendCategoryOptions[0]?.id ?? null,
    [categoryInsights, topDiscretionaryCategory?.id, trendCategoryOptions],
  );

  const flow = useCategoryMonthlyFlow({
    cycleEnd: cycleRange.end,
    initialCategoryId: defaultTrendCategoryId,
    options: trendCategoryOptions,
  });

  const { data: transactionTypes = [] } = useTransactionTypes();
  const expenseTypeId = useMemo(
    () => transactionTypes.find((type) => type.name === "EXPENSE")?.id,
    [transactionTypes],
  );

  const selectedCategoryBreakdown = useMemo(() => {
    if (!selectedCategory) return null;
    return buildCategoryTransactionBreakdown(selectedCategoryTransactions, expenseTypeId);
  }, [expenseTypeId, selectedCategory, selectedCategoryTransactions]);

  const selectedCategoryDriverInsights = useMemo(() => {
    if (!selectedCategory) return [];

    const insights: string[] = [];
    const breakdown = selectedCategoryBreakdown;
    const isTopIssue = selectedCategory.id === topDiscretionaryCategory?.id;

    if (isTopIssue && selectedCategory.status === "over") {
      insights.push(t("analytics.modalInsightTopOverspend"));
    }

    if (
      selectedCategory.budgetAmount &&
      selectedCategory.usagePercent != null
    ) {
      if (selectedCategory.status === "over") {
        insights.push(
          t("analytics.modalInsightAboveBudget", {
            percent: formatPercent((selectedCategory.usagePercent - 1) * 100),
          }),
        );
      } else {
        insights.push(
          t("analytics.modalInsightBudgetUsage", {
            percent: formatPercent(selectedCategory.usagePercent * 100),
          }),
        );
      }
    }

    insights.push(
      t("analytics.modalInsightShareOfSpend", {
        percent: formatPercent(selectedCategory.percentage),
      }),
    );

    if (loadingCategoryTransactions) {
      return insights;
    }

    if (breakdown && breakdown.transactionCount > 0) {
      insights.push(
        t("analytics.topChargesShareSummary", {
          percent: formatPercent(breakdown.topThreeShare * 100),
        }),
      );
    } else if (selectedCategory.spent > 0) {
      insights.push(t("analytics.noDetailedTransactionsInsight"));
    }

    return insights.slice(0, 4);
  }, [
    loadingCategoryTransactions,
    selectedCategory,
    selectedCategoryBreakdown,
    t,
    topDiscretionaryCategory?.id,
  ]);

  const assignedCategoryIds = useMemo(
    () =>
      (budgetData?.budgets_balance ?? [])
        .map((budget) => budget.category_id)
        .filter((id): id is number => typeof id === "number"),
    [budgetData?.budgets_balance],
  );

  const getCategoryStatusLabel = (category: CategoryInsight) => {
    if (category.status === "over") return t("analytics.statusOverBudget");
    if (category.status === "near") return t("analytics.statusNearLimit");
    if (category.status === "safe") return t("analytics.statusInControl");
    return t("analytics.noBudget");
  };

  const getCardUsageCopy = (category: CategoryInsight) =>
    getBudgetUsageLabel({
      category,
      t,
      formatCurrency,
      formatPercent,
    });

  const renderSavingsDistributionCard = () => {
    if (!FEATURE_FLAGS.savingPockets) return null;

    return (
      <SavingsDistributionCard
        isLoading={isLoadingSavingPockets}
        data={savingsDistributionData}
        total={savingsDistributionTotal}
        colors={savingsDistributionColors}
        pieColors={PIE_COLORS}
        summary={
          <ShareSummary
            label={t("common.labels.total")}
            value={formatCurrency(savingsDistributionTotal)}
            segments={toShareSegments(
              savingsDistributionData,
              savingsDistributionColors,
              PIE_COLORS,
            )}
          />
        }
      />
    );
  };

  const rankingSorts = availableRankingSorts(pieMetric);
  const activeRankingSort: RankingSort = rankingSorts.includes(rankingSort) ? rankingSort : "amount";
  const categoryPieTitle =
    pieMetric === "income" ? t("analytics.incomeByCategory") : t("analytics.expenseByCategory");
  const categoryPieSubtitle =
    pieMetric === "income"
      ? t("analytics.incomeByCategorySubtitle")
      : t("analytics.expenseByCategorySubtitle");
  const accountPieTitle =
    pieMetric === "income" ? t("analytics.incomeByAccount") : t("analytics.expenseByAccount");
  const accountPieSubtitle =
    pieMetric === "income"
      ? t("analytics.incomeByAccountSubtitle")
      : t("analytics.expenseByAccountSubtitle");

  const selectPieMetric = (id: string) => {
    setPieMetric(id as PieMetric);
    setHiddenCategoryIds([]);
    setHiddenAccountIds([]);
  };

  const totalLabel = (hiddenCount: number) =>
    hiddenCount > 0 ? t("analytics.visibleTotalLabel") : t("common.labels.total");

  // Same accessible summary the old share bars used: "Comida 42%, Renta 58%".
  const distributionSummary = (segments: DistributionSegment[]) => {
    const total = segments.reduce((sum, segment) => sum + segment.value, 0);
    return segments
      .map((segment) => `${segment.label} ${total > 0 ? Math.round((segment.value / total) * 100) : 0}%`)
      .join(", ");
  };

  const renderRestore = (count: number, onRestore: () => void) =>
    count > 0 ? (
      <div className="mb-2 flex justify-end">
        <button
          type="button"
          onClick={onRestore}
          className="app-focus-ring min-h-11 text-sm font-medium text-[var(--ds-ink)] underline underline-offset-4"
        >
          {t("analytics.hiddenCountRestore", { count })}
        </button>
      </div>
    ) : null;

  const renderShareRows = ({
    items,
    hiddenIds,
    visibleTotal,
    colorOf,
    onToggle,
    onSelect,
    meta,
  }: {
    items: SeriesDatum[];
    hiddenIds: HiddenPieItems;
    visibleTotal: number;
    colorOf: (item: SeriesDatum) => string;
    onToggle: (id: number | string) => void;
    onSelect?: (id: number) => void;
    meta?: (id: number | string) => string | undefined;
  }) =>
    items.map((item) => {
      const isHidden = hiddenIds.includes(item.id);
      const share = !isHidden && visibleTotal > 0 ? (item.value / visibleTotal) * 100 : 0;
      return (
        <RankingRow
          key={`ranking-${item.id}`}
          label={item.name}
          color={colorOf(item)}
          amount={formatSignedCurrency(pieMetric === "expense" ? -item.value : item.value)}
          share={isHidden ? t("analytics.hiddenFromChart") : formatPercent(share)}
          bar={Math.min(100, Math.round(share * 2))}
          shown={!isHidden}
          onToggleShown={() => onToggle(item.id)}
          toggleLabel={
            isHidden
              ? t("analytics.toggleSeriesShow", { name: item.name })
              : t("analytics.toggleSeriesHide", { name: item.name })
          }
          onSelect={onSelect && typeof item.id === "number" ? () => onSelect(item.id as number) : undefined}
          meta={meta?.(item.id)}
        />
      );
    });

  const describeCategoryRanking = (id: number | string): string | undefined => {
    const entry = categoryRankingMeta.get(id);
    if (activeRankingSort === "overspend") {
      return entry?.overage && entry.overage > 0
        ? t("analytics.rankingOverspendMeta", { amount: formatCurrency(entry.overage) })
        : t("analytics.rankingNoOverspendMeta");
    }
    if (activeRankingSort === "variance") {
      const variance = entry?.variance;
      if (variance == null) return t("analytics.rankingNoVarianceMeta");
      return t("analytics.rankingVarianceMeta", { amount: formatSignedCurrency(variance) });
    }
    return undefined;
  };

  const sortedCategoryItems = sortRankingItems(categoryPieData, categoryRankingMeta, activeRankingSort);
  const categorySegments: DistributionSegment[] = sortedCategoryItems
    .filter((item) => !hiddenCategoryIds.includes(item.id))
    .map((item) => ({
      id: String(item.id),
      label: item.name,
      value: item.value,
      color: categoryColorById.get(item.id) ?? PIE_COLORS[0],
    }));

  const accountPieData = pieMetric === "income" ? accountIncomePieData : accountExpensePieData;
  const sortedAccountItems = [...accountPieData].sort((a, b) => b.value - a.value);
  const visibleAccountPieData = sortedAccountItems.filter((item) => !hiddenAccountIds.includes(item.id));
  const visibleAccountTotal = visibleAccountPieData.reduce((sum, item) => sum + item.value, 0);
  const accountColorById = buildColorMap(accountPieData, PIE_COLORS);
  const accountSegments: DistributionSegment[] = visibleAccountPieData.map((item) => ({
    id: String(item.id),
    label: item.name,
    value: item.value,
    color: accountColorById.get(item.id) ?? PIE_COLORS[0],
  }));

  const renderCategoryBody = () => {
    if (loadingCategoryBalance && categoryPieData.length === 0) {
      return (
        <div className="space-y-3">
          <SkeletonBlock className="h-16 w-full" />
          <SkeletonBlock className="h-64 w-full" />
        </div>
      );
    }
    if (categoryPieData.length === 0) {
      return <p className="py-10 text-center text-sm text-[var(--ds-ink-muted)]">{t("analytics.noActivity")}</p>;
    }

    return (
      <>
        {visibleCategoryTotal > 0 ? (
          <>
            <TotalLine label={totalLabel(hiddenCategoryIds.length)} value={formatCurrency(visibleCategoryTotal)} />
            <DistributionBar segments={categorySegments} ariaLabel={distributionSummary(categorySegments)} />
          </>
        ) : (
          <p className="py-6 text-center text-sm text-[var(--ds-ink-muted)]">{t("analytics.noVisibleCategories")}</p>
        )}
        {rankingSorts.length > 1 ? (
          <div className="mt-4">
            <SortPills
              options={rankingSorts.map((id) => ({ id, label: t(`analytics.rankingSort.${id}`) }))}
              value={activeRankingSort}
              onChange={(id) => setRankingSort(id as RankingSort)}
              label={t("analytics.rankingSortLabel")}
              ariaLabel={t("analytics.rankingSortLabel")}
            />
          </div>
        ) : null}
        <div className="mt-4">
          {renderRestore(hiddenCategoryIds.length, () => setHiddenCategoryIds([]))}
          {renderShareRows({
            items: sortedCategoryItems,
            hiddenIds: hiddenCategoryIds,
            visibleTotal: visibleCategoryTotal,
            colorOf: (item) => categoryColorById.get(item.id) ?? PIE_COLORS[0],
            onToggle: (id) => toggleHiddenItem(id, hiddenCategoryIds, setHiddenCategoryIds),
            onSelect: setSelectedCategoryId,
            meta: rankingSorts.length > 1 && activeRankingSort !== "amount" ? describeCategoryRanking : undefined,
          })}
        </div>
      </>
    );
  };

  const renderAccountBody = () => {
    if (loadingAccountsBalance && accountPieData.length === 0) {
      return <SkeletonBlock className="h-64 w-full" />;
    }
    if (accountPieData.length === 0) {
      return <p className="py-10 text-center text-sm text-[var(--ds-ink-muted)]">{t("analytics.noActivity")}</p>;
    }

    return (
      <>
        {visibleAccountTotal > 0 ? (
          <>
            <TotalLine label={totalLabel(hiddenAccountIds.length)} value={formatCurrency(visibleAccountTotal)} />
            <DistributionBar segments={accountSegments} ariaLabel={distributionSummary(accountSegments)} />
          </>
        ) : (
          <p className="py-6 text-center text-sm text-[var(--ds-ink-muted)]">{t("analytics.noVisibleItems")}</p>
        )}
        <div className="mt-4">
          {renderRestore(hiddenAccountIds.length, () => setHiddenAccountIds([]))}
          {renderShareRows({
            items: sortedAccountItems,
            hiddenIds: hiddenAccountIds,
            visibleTotal: visibleAccountTotal,
            colorOf: (item) => accountColorById.get(item.id) ?? PIE_COLORS[0],
            onToggle: (id) => toggleHiddenItem(id, hiddenAccountIds, setHiddenAccountIds),
          })}
        </div>
      </>
    );
  };

  const renderCategoriesPanel = () => (
    <div className="grid gap-6">
      <Tabs
        variant="kind"
        items={[
          { id: "expense", label: t("analytics.expenseMetric") },
          { id: "income", label: t("analytics.incomeMetric") },
        ]}
        value={pieMetric}
        onChange={selectPieMetric}
        ariaLabel={t("analytics.kindLabel")}
      />
      <div className="grid items-start gap-6 md:grid-cols-2">
        <AnalyticsCard overline={t("analytics.viewCategories")} title={categoryPieTitle} description={categoryPieSubtitle}>
          {renderCategoryBody()}
        </AnalyticsCard>
        <AnalyticsCard overline={t("analytics.accountsOverline")} title={accountPieTitle} description={accountPieSubtitle}>
          {renderAccountBody()}
        </AnalyticsCard>
      </div>
    </div>
  );

  const flowCategory = flow.selectedCategory;
  const flowLast = flow.points[flow.points.length - 1];
  const flowHasActivity = flow.points.some((point) => point.expense > 0 || point.income > 0);
  const flowMetrics: { label: string; value: string; tone: "neutral" | "negative" | "positive" }[] = [
    { tone: "neutral", label: t("analytics.trendRangeLabel"), value: t("analytics.trendRangeValue", { count: flow.points.length }) },
    { tone: "negative", label: t("analytics.latestExpenseLabel"), value: formatSignedCurrency(-(flowLast?.expense ?? 0)) },
    { tone: "positive", label: t("analytics.latestIncomeLabel"), value: formatSignedCurrency(flowLast?.income ?? 0) },
    { tone: "negative", label: t("analytics.averageExpenseLabel"), value: formatSignedCurrency(-flow.averages.expense) },
    { tone: "positive", label: t("analytics.averageIncomeLabel"), value: formatSignedCurrency(flow.averages.income) },
  ];
  const flowAriaLabel = flowCategory
    ? [
        `${flowCategory.name}. ${t("analytics.categoryTrendTitle")}`,
        ...flow.points.map(
          (point) =>
            `${point.label}: ${t("analytics.expenseSeriesLabel")} ${formatCurrency(point.expense)}, ${t("analytics.incomeSeriesLabel")} ${formatCurrency(point.income)}`,
        ),
      ].join(". ")
    : "";

  const renderFlowBody = () => {
    if (flow.isLoading && !flow.hasResponse) {
      return (
        <div className="mt-4 rounded-[var(--ds-radius-md)] border-[1.5px] border-solid border-[var(--ds-line)] p-4 text-sm text-[var(--ds-ink-muted)]">
          {t("loadingShort")}
        </div>
      );
    }
    if (!flowCategory) {
      return <p className="py-10 text-center text-sm text-[var(--ds-ink-muted)]">{t("analytics.noActivity")}</p>;
    }
    if (!flowHasActivity) {
      return <p className="py-10 text-center text-sm text-[var(--ds-ink-muted)]">{t("analytics.categoryTrendEmptySeries")}</p>;
    }

    return (
      <>
        <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5">
          {flowMetrics.map((metric) => (
            <div
              key={metric.label}
              className="min-w-0 rounded-[var(--ds-radius-md)] border-[1.5px] border-solid border-[var(--ds-line)] px-4 py-3"
            >
              <p className="text-[13px] leading-[18px] text-[var(--ds-ink-muted)]">{metric.label}</p>
              <p className={`mt-1 break-words text-[20px] font-medium leading-7 tabular-nums ${metric.tone === "negative" ? "text-[var(--ds-negative)]" : metric.tone === "positive" ? "text-[var(--ds-positive)]" : "text-[var(--ds-ink)]"}`}>
                {metric.value}
              </p>
            </div>
          ))}
        </div>
        <div className="relative mt-4">
          <FlowChartWide
            labels={flow.points.map((point) => point.label)}
            expense={flow.points.map((point) => point.expense)}
            income={flow.points.map((point) => point.income)}
            show={flowSeries}
            activeIndex={flow.points.length - 1}
            ariaLabel={flowAriaLabel}
            seriesLabels={{ expense: t("analytics.expenseSeriesLabel"), income: t("analytics.incomeSeriesLabel") }}
          />
          {flow.isFetching ? (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-[var(--ds-radius-md)] bg-[var(--app-loading-overlay-bg)] text-sm font-medium text-[var(--ds-ink)]">
              {t("loadingShort")}
            </div>
          ) : null}
        </div>
      </>
    );
  };

  const renderFlowPanel = () => (
    <AnalyticsCard
      title={t("analytics.categoryTrendTitle")}
      description={t("analytics.categoryTrendSubtitle")}
      aside={
        flowCategory ? (
          <button
            type="button"
            onClick={() => setSelectedCategoryId(flowCategory.id)}
            className="app-focus-ring inline-flex min-h-11 items-center rounded-[var(--ds-radius-pill)] border-[1.5px] border-solid border-[var(--ds-ink)] bg-transparent px-5 text-[15px] font-medium text-[var(--ds-ink)]"
          >
            {t("analytics.reviewCategoryDetails")}
          </button>
        ) : null
      }
    >
      {trendCategoryOptions.length > 0 ? (
        <div className="grid gap-4">
          <FormField label={t("analytics.categoryTrendSelectLabel")} htmlFor="category-trend-select">
            <SelectInput
              id="category-trend-select"
              className="border-[1.5px] border-solid border-[var(--ds-ink)]"
              value={flowCategory?.id ?? ""}
              onChange={(event) => flow.selectCategory(Number(event.target.value))}
            >
              {trendCategoryOptions.map((category: TrendCategoryOption) => (
                <option key={`trend-option-${category.id}`} value={category.id}>
                  {category.name}
                </option>
              ))}
            </SelectInput>
          </FormField>
          <div role="group" aria-label={t("analytics.flowSeriesLabel")} className="flex gap-2">
            {(["expense", "income"] as const).map((series) => {
              const selected = flowSeries === "both" || flowSeries === series;
              return (
                <button
                  key={series}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setFlowSeries((current) => current === "both" ? (series === "expense" ? "income" : "expense") : current === series ? current : "both")}
                  className={`app-focus-ring inline-flex min-h-11 items-center gap-1 rounded-full border-[1.5px] border-solid px-5 text-[15px] leading-none ${selected ? "border-[var(--ds-card-sun)] bg-[var(--ds-card-sun)] font-medium text-[var(--ds-on-card)]" : "border-[var(--ds-ink)] bg-transparent text-[var(--ds-ink)]"}`}
                >
                  <span aria-hidden="true">{series === "expense" ? "●" : "■"}</span>
                  {t(series === "expense" ? "analytics.expenseSeriesLabel" : "analytics.incomeSeriesLabel")}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
      {renderFlowBody()}
    </AnalyticsCard>
  );

  const installmentColorById = buildColorMap(
    [...activeInstallmentPieData, ...monthlyInstallmentPieData],
    PIE_COLORS,
  );
  const visibleActiveInstallments = activeInstallmentPieData.filter(
    (item) => !hiddenInstallmentIds.includes(item.id),
  );
  const visibleMonthlyInstallments = monthlyInstallmentPieData.filter(
    (item) => !hiddenInstallmentIds.includes(item.id),
  );
  const activeInstallmentTotal = visibleActiveInstallments.reduce((sum, item) => sum + item.value, 0);
  const monthlyInstallmentTotal = visibleMonthlyInstallments.reduce((sum, item) => sum + item.value, 0);
  const hasUnknownInterest = (installmentsBalanceData?.installments_balance ?? []).some(
    (installment) => isIncompleteInstallment(installment) && installment.interest_rate == null,
  );

  const installmentRows = (installmentsBalanceData?.installments_balance ?? [])
    .filter(isIncompleteInstallment)
    .map((installment) => ({
      id: installment.id,
      name: installment.name,
      accountName:
        accountNameById.get(installment.account_id) ??
        `${t("common.labels.account")} ${installment.account_id}`,
      monthly: Number(installment.monthly_expected ?? 0),
      remaining: Number(installment.amount_left ?? 0),
    }))
    .filter((row) => row.remaining > 0)
    .sort((a, b) => b.remaining - a.remaining);
  const installmentShapeById = new Map(
    installmentRows.map((row, index) => [row.id, index % 2 === 0 ? ("square" as const) : ("circle" as const)]),
  );
  const installmentGroups = Array.from(
    installmentRows.reduce((groups, row) => {
      groups.set(row.accountName, [...(groups.get(row.accountName) ?? []), row]);
      return groups;
    }, new Map<string, typeof installmentRows>()),
  )
    .map(([accountName, items]) => ({
      accountName,
      items,
      monthly: items.reduce((sum, item) => sum + item.monthly, 0),
      remaining: items.reduce((sum, item) => sum + item.remaining, 0),
    }))
    .sort((a, b) => b.remaining - a.remaining);

  const renderInstallmentBar = (data: SeriesDatum[], visible: SeriesDatum[], total: number) => {
    if (data.length === 0) {
      return <p className="py-6 text-center text-sm text-[var(--ds-ink-muted)]">{t("analytics.noActivity")}</p>;
    }
    if (visible.length === 0 || total === 0) {
      return <p className="py-6 text-center text-sm text-[var(--ds-ink-muted)]">{t("analytics.noVisibleItems")}</p>;
    }

    const segments: DistributionSegment[] = visible.map((item) => ({
      id: String(item.id),
      label: item.name,
      value: item.value,
      color: installmentColorById.get(item.id) ?? PIE_COLORS[0],
    }));
    return (
      <>
        <TotalLine label={totalLabel(hiddenInstallmentIds.length)} value={formatCurrency(total)} />
        <DistributionBar segments={segments} ariaLabel={distributionSummary(segments)} shapes />
      </>
    );
  };

  const renderInstallmentsPanel = () => {
    if (loadingInstallmentsBalance) {
      return <SkeletonBlock className="h-64 w-full" />;
    }

    return (
      <div className="grid gap-6">
        {hasUnknownInterest ? (
          <InfoNote title={t("analytics.incompleteRatesTitle")}>{t("analytics.incompleteRatesText")}</InfoNote>
        ) : null}
        <div className="grid items-start gap-6 md:grid-cols-2">
          <AnalyticsCard title={t("analytics.activeInstallmentsChart")} description={t("analytics.activeInstallmentsChartSubtitle")}>
            {renderInstallmentBar(activeInstallmentPieData, visibleActiveInstallments, activeInstallmentTotal)}
          </AnalyticsCard>
          <AnalyticsCard title={t("analytics.monthlyInstallmentsChart")} description={t("analytics.monthlyInstallmentsChartSubtitle")}>
            {renderInstallmentBar(monthlyInstallmentPieData, visibleMonthlyInstallments, monthlyInstallmentTotal)}
          </AnalyticsCard>
        </div>
        <AnalyticsCard
          title={t("analytics.installmentsListTitle")}
          description={t("analytics.installmentsListSubtitle")}
          aside={t("analytics.installmentsCount", { count: installmentRows.length })}
          asideKind="note"
        >
          {renderRestore(hiddenInstallmentIds.length, () => setHiddenInstallmentIds([]))}
          {installmentRows.length === 0 ? (
            <p className="py-6 text-center text-sm text-[var(--ds-ink-muted)]">{t("analytics.noActivity")}</p>
          ) : (
            installmentGroups.map((group, index) => (
              <SeriesGroup
                key={group.accountName}
                first={index === 0}
                label={`${group.accountName} · ${formatCurrency(group.monthly)} / ${formatCurrency(group.remaining)}`}
              >
                {group.items.map((row) => {
                  const isHidden = hiddenInstallmentIds.includes(row.id);
                  return (
                    <SeriesRow
                      key={`installment-${row.id}`}
                      label={row.name}
                      color={installmentColorById.get(row.id) ?? PIE_COLORS[0]}
                      shape={installmentShapeById.get(row.id) ?? "square"}
                      amount={t("analytics.installmentsAmountPair", {
                        monthly: formatCurrency(row.monthly),
                        remaining: formatCurrency(row.remaining),
                      })}
                      caption={t("analytics.installmentsAmountPairLegend")}
                      shown={!isHidden}
                      onToggleShown={() => toggleHiddenItem(row.id, hiddenInstallmentIds, setHiddenInstallmentIds)}
                      toggleLabel={
                        isHidden
                          ? t("analytics.toggleSeriesShow", { name: row.name })
                          : t("analytics.toggleSeriesHide", { name: row.name })
                      }
                    />
                  );
                })}
              </SeriesGroup>
            ))
          )}
        </AnalyticsCard>
      </div>
    );
  };

  const installmentsModeBar = (
    <ModeBar label={t("analytics.installmentsOverviewLabel")} value={t("analytics.windowToDate")} />
  );

  const selectedCategoryUsageCopy = selectedCategory
    ? getCardUsageCopy(selectedCategory)
    : null;
  const selectedCategoryOverspendCopy =
    selectedCategory?.budgetAmount && selectedCategory.status === "over"
      ? getOverspendMessage({
          category: selectedCategory,
          t,
          formatCurrency,
          formatPercent,
        })
      : null;

  const cycleOptions = [
    { id: "current", label: t("common.periods.thisMonth") },
    { id: "previous", label: t("common.periods.lastMonth") },
    { id: "custom", label: t("analytics.cycleCustomMonth") },
  ];
  const selectCycle = (id: string) => setCycle(id as CycleFilter);
  const selectView = (id: string) => setAnalyticsView(id as AnalyticsViewTab);

  const selectedMonthLabel =
    selectableMonths.find((month) => month.value === customMonth)?.label ?? customMonth;
  const cycleButtonText = t("analytics.cycleButtonLabel", {
    cycle: cycle === "custom"
      ? selectedMonthLabel
      : cycle === "previous"
        ? t("common.periods.lastMonth")
        : t("common.periods.thisMonth"),
  });

  const customMonthField = (
    <FormField
      label={t("analytics.cycleMonthLabel")}
      htmlFor="analytics-cycle-month"
      className="app-custom-cycle-panel w-full md:w-56"
    >
      <SelectInput
        id="analytics-cycle-month"
        value={customMonth}
        onChange={(event) => setCustomMonth(event.target.value)}
        className="bg-[var(--app-panel-muted-bg)] text-sm font-medium"
      >
        {selectableMonths.map((month) => (
          <option key={month.value} value={month.value}>
            {month.label}
          </option>
        ))}
      </SelectInput>
    </FormField>
  );

  // Resumen: el ciclo vive en un botón que despliega las mismas opciones.
  const summaryCyclePicker = (
    <div
      ref={cycleMenuRef}
      className="relative"
      onKeyDown={(event) => {
        if (event.key === "Escape") setCycleMenuOpen(false);
      }}
    >
      <PillButton
        icon={<FiCalendar className="size-[18px]" />}
        chevron
        aria-haspopup="menu"
        aria-expanded={cycleMenuOpen}
        aria-controls="analytics-cycle-menu"
        onClick={() => setCycleMenuOpen((open) => !open)}
      >
        {cycleButtonText}
      </PillButton>
      {cycleMenuOpen && (
        <div
          id="analytics-cycle-menu"
          role="group"
          aria-label={t("analytics.cycleMenuLabel")}
          className="absolute right-0 z-20 mt-2 flex w-[min(420px,calc(100vw-32px))] flex-col gap-3 rounded-[var(--ds-radius-lg)] border-[1.5px] border-solid border-[var(--ds-line)] bg-[var(--app-surface)] p-4 shadow-[var(--ds-shadow-2)]"
        >
          <Tabs variant="cycle" items={cycleOptions} value={cycle} onChange={selectCycle} ariaLabel={t("analytics.cycleMenuLabel")} />
          {cycle === "custom" ? customMonthField : null}
        </div>
      )}
    </div>
  );

  // Categorías y Flujo: mismas opciones de ciclo como segmentado; el mes libre aparece debajo.
  const sharedCycleTabs = (
    <div className="flex w-full flex-col items-stretch gap-3 md:w-auto md:items-end">
      <Tabs variant="cycle" items={cycleOptions} value={cycle} onChange={selectCycle} ariaLabel={t("analytics.cycleMenuLabel")} />
      {cycle === "custom" ? customMonthField : null}
    </div>
  );

  const headerAside =
    analyticsView === "summary" ? summaryCyclePicker
    : analyticsView === "installments" ? installmentsModeBar
    : sharedCycleTabs;

  const { topOverspentCategory } = budgetPressure;

  const splitStatus = topOverspentCategory
    ? {
        tone: "warning" as const,
        title: t("analytics.reduceDiscretionaryCategory", {
          category: topOverspentCategory.name,
          amount: formatCurrency(topOverspentCategory.overage),
        }),
        detail: t("analytics.discretionaryActionHint", {
          count: budgetPressure.overspentCategories.length,
          amount: formatCurrency(budgetPressure.totalOverage),
        }),
      }
    : {
        tone: "ok" as const,
        title: t("analytics.discretionaryOnTrack"),
        detail: budgetPressure.unbudgetedSpend > 0
          ? t("analytics.unbudgetedSpendHint", { amount: formatCurrency(budgetPressure.unbudgetedSpend) })
          : undefined,
      };

  const splitLabels = {
    overline: t("analytics.committedSpendLabel"),
    title: t("analytics.committedSpendTitle"),
    description: t("analytics.committedSpendSubtitle"),
    committed: t("analytics.committedLabel"),
    discretionary: t("analytics.discretionaryLabel"),
    committedNote: t("analytics.splitCommittedNote"),
    discretionaryNote: t("analytics.discretionaryDetail"),
    percentOfSpend: (percent: string) => t("analytics.splitPercentOfSpend", { percent }),
  };

  const totalBudget = (budgetData?.budgets_balance ?? []).reduce(
    (sum, budget) => sum + Number(budget.amount ?? 0),
    0,
  );
  const spentThisCycle = cycleSpendBreakdown.totalSpend;
  const usedPercent = totalBudget > 0 ? Math.round((spentThisCycle / totalBudget) * 100) : 0;

  // Sin dato del ciclo anterior con la misma definición de gasto: no se muestra variación.
  const heroValue = loadingCycleSpend ? "—" : `↓ ${formatSignedCurrency(-spentThisCycle)}`;
  const heroText = loadingCycleSpend
    ? "—"
    : totalBudget > 0
      ? t("analytics.heroSummary", { amount: formatCurrency(spentThisCycle), percent: usedPercent })
      : t("analytics.heroSummaryNoBudget", { amount: formatCurrency(spentThisCycle) });
  const budgetNote = loadingBudgetBalance
    ? "—"
    : totalBudget > 0
      ? t("analytics.budgetUsedNote", { percent: usedPercent })
      : t("analytics.noBudget");

  if (loadingCategoryBalance && !categoryBalanceData) {
    return <PageSkeleton lines={6} />;
  }

  return (
    <div className="app-page">
      <div className="app-container">
        <div className="mb-8 md:mb-10">
          <AnalyticsHeader
            overline={t("analytics.monthlyReadingLabel")}
            title={[t("common:labels.greeting"), `${t("common:nav.analytics")}!`]}
            subtitle={t(`analytics.viewSubtitle.${analyticsView}`)}
            aside={headerAside}
          />
        </div>

        <div className="mb-6">
          <Tabs
            variant="view"
            idPrefix="analytics-view"
            value={analyticsView}
            onChange={selectView}
            ariaLabel={t("analytics.title")}
            items={[
              { id: "summary", label: t("analytics.viewSummary") },
              { id: "categories", label: t("analytics.viewCategories") },
              { id: "flow", label: t("analytics.viewFlow") },
              { id: "installments", label: t("analytics.viewInstallments") },
            ]}
          />
        </div>

        <section
          id="analytics-view-panel-summary"
          role="tabpanel"
          aria-label={t("analytics.viewSummary")}
          tabIndex={0}
          hidden={analyticsView !== "summary"}
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-[1.5fr_1fr_1fr] md:gap-5">
            <CycleHero label={t("analytics.summaryHeroLabel")} value={heroValue}>
              {heroText}
            </CycleHero>
            <MetricTile
              label={t("analytics.totalIncomeKpi")}
              value={loadingCategoryBalance ? "—" : formatSignedCurrency(totalCategoryIncome)}
            />
            <MetricTile
              label={t("analytics.totalBudgetKpi")}
              value={loadingBudgetBalance ? "—" : formatCurrency(totalBudget)}
              note={budgetNote}
            />
          </div>

          <SectionIntro
            title={t("analytics.splitSectionTitle")}
            subtitle={t("analytics.splitSectionSubtitle")}
          />
          <div className="max-w-[860px]">
            {loadingCycleSpend ? (
              <SkeletonBlock className="h-48 w-full" />
            ) : (
              <SplitSummary
                committed={cycleSpendBreakdown.committed}
                discretionary={cycleSpendBreakdown.discretionary}
                status={splitStatus}
                labels={splitLabels}
              />
            )}
            {!loadingCycleSpend && topOverspentCategory ? (
              <button
                type="button"
                onClick={() => setSelectedCategoryId(topOverspentCategory.id)}
                className="mt-4 inline-flex min-h-11 items-center rounded-[var(--ds-radius-pill)] border-[1.5px] border-solid border-[var(--ds-ink)] bg-transparent px-5 text-[15px] font-medium text-[var(--ds-ink)]"
              >
                {t("analytics.reviewCategoryDetails")}
              </button>
            ) : null}
          </div>

          {FEATURE_FLAGS.savingPockets ? (
            <div className="mt-6 grid grid-cols-1 gap-4 sm:gap-5 lg:grid-cols-2 xl:grid-cols-12 xl:items-start xl:gap-6">
              {renderSavingsDistributionCard()}
            </div>
          ) : null}
        </section>

        <section
          id="analytics-view-panel-categories"
          role="tabpanel"
          aria-label={t("analytics.viewCategories")}
          tabIndex={0}
          hidden={analyticsView !== "categories"}
        >
          {renderCategoriesPanel()}
        </section>

        <section
          id="analytics-view-panel-flow"
          role="tabpanel"
          aria-label={t("analytics.viewFlow")}
          tabIndex={0}
          hidden={analyticsView !== "flow"}
        >
          {renderFlowPanel()}
        </section>

        <section
          id="analytics-view-panel-installments"
          role="tabpanel"
          aria-label={t("analytics.viewInstallments")}
          tabIndex={0}
          hidden={analyticsView !== "installments"}
        >
          {renderInstallmentsPanel()}
        </section>

        <CategoryDetailModal
          selectedCategory={selectedCategory}
          onClose={() => setSelectedCategoryId(null)}
          onEditBudget={budgetEditor.open}
          getCategoryStatusLabel={getCategoryStatusLabel}
          usageCopy={selectedCategoryUsageCopy}
          overspendCopy={selectedCategoryOverspendCopy}
          driverInsights={selectedCategoryDriverInsights}
          breakdown={selectedCategoryBreakdown}
          loadingTransactions={loadingCategoryTransactions}
        />

        <NewBudgetModal
          isOpen={budgetEditor.isOpen}
          onClose={budgetEditor.close}
          categories={categories}
          assignedCategoryIds={assignedCategoryIds}
          initialData={budgetEditor.state}
          onSubmit={async (data) => {
            try {
              if (budgetEditor.state?.id) {
                await budgetEditor.updateMutation.mutateAsync({
                  id: budgetEditor.state.id,
                  data,
                });
              } else {
                await budgetEditor.createMutation.mutateAsync(data);
              }

              budgetEditor.close();
            } catch {
              showAppMessage({
                title: t("analytics.saveBudgetError"),
                tone: "danger",
              });
            }
          }}
        />
      </div>
    </div>
  );
};
